import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { LookupItemStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { ProdutoArquivoStruct } from '../../../../../shared/services/structs/produto/produto-arquivo.struct';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { ProdutoService } from '../../../../../shared/services/apis/produto/produto.service';
import { GetArquivoProdutoResponse } from '../../../../../shared/services/responses/produto/get-arquivo-produto.response';

@Component({
  selector: 'app-produto-complementares',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
  ],
  templateUrl: './produto-complementares.component.html',
  styleUrl: './produto-complementares.component.css',
})
export class ProdutoComplementaresComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input() listaCategoria: LookupItemStruct[] = [];
  @Input() listaMarca: LookupItemStruct[] = [];
  @Input() anexos: ProdutoArquivoStruct[] = [];
  @Output() anexosChange = new EventEmitter<ProdutoArquivoStruct[]>();

  public readonly MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10MB

  constructor(
    private dialog: MatDialog,
    private alertService: AlertService,
    private produtoService: ProdutoService
  ) {}

  get idCategoriaControl() {
    return this.form.get('idCategoria') as FormControl;
  }
  get idMarcaControl() {
    return this.form.get('idMarca') as FormControl;
  }

  public setCapa(index: number, event: any): void {
    this.anexos.forEach((anexo, i) => {
      anexo.isCapa = i === index && event.checked;
    });
    this.anexosChange.emit(this.anexos);
  }

  public isImageFile(file: ProdutoArquivoStruct): boolean {
    const ext = file.extensaoArquivo || this.getExtensao(file.nomeArquivo);
    if (!ext) return false;
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    return imageExts.includes(
      ext.toLowerCase().startsWith('.') ? ext.toLowerCase() : '.' + ext.toLowerCase()
    );
  }

  private getExtensao(nomeArquivo: string): string {
    if (!nomeArquivo) return '';
    const lastDot = nomeArquivo.lastIndexOf('.');
    return lastDot >= 0 ? nomeArquivo.substring(lastDot) : '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(input.files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  formatBytes(bytes: number | null | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  processFiles(files: FileList): void {
    const currentTotalSize = this.anexos.reduce((acc, curr) => acc + (curr.tamanhoBytes || 0), 0);
    let newFilesSize = 0;

    // Calculate size of new files
    for (let i = 0; i < files.length; i++) {
      newFilesSize += files[i].size;
    }

    if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
      this.alertService.show(
        'Aviso',
        'O tamanho total dos arquivos excede o limite de 10MB.',
        AlertType.warning
      );
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > this.MAX_FILE_SIZE) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" excede o limite de 2MB.`,
          AlertType.warning
        );
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result.split(',')[1];
        this.anexos.push({
          nomeArquivo: file.name,
          extensaoArquivo: this.getExtensao(file.name),
          tamanhoBytes: file.size,
          base64: base64String,
          isCapa: this.anexos.length === 0, // First one is cover
          ordem: this.anexos.length + 1,
        });
        this.anexosChange.emit(this.anexos);
      };
      reader.readAsDataURL(file);
    }
  }

  limparAnexos(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação',
        description: 'Deseja realmente remover todos os anexos?',
        buttonConfirm: 'Sim',
        buttonClose: 'Não',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.anexos = [];
        this.anexosChange.emit(this.anexos);
      }
    });
  }

  removeAnexo(index: number): void {
    this.anexos.splice(index, 1);
    this.anexosChange.emit(this.anexos);
  }

  public downloadAnexo(index: number): void {
    const arquivo = this.anexos[index];
    if (!arquivo.urlArquivo) return;

    this.produtoService.getArquivo(arquivo.urlArquivo).subscribe({
      next: (response: GetArquivoProdutoResponse) => {
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.error);
          return;
        }
        const link = document.createElement('a');
        link.href = response.urlArquivo;
        link.download = arquivo.nomeArquivo;
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao baixar arquivo.', AlertType.error);
      },
    });
  }
}
